// recovr motion — scroll reveals, stat bars, step timelines, sticky bars.
// Declarative via data attributes so sections stay markup-only:
//   [data-recovr-reveal]              fade/rise in on view (stagger within a parent)
//   [data-recovr-bar="92"]            fill a bar to N% when scrolled into view
//   [data-recovr-step]                timeline step: gains .on near viewport center
//   [data-recovr-sticky="#selector"]  fixed bar: gains .show once target scrolls away
// Everything is disabled under prefers-reduced-motion (end state applied instantly).

const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
const seen = new WeakSet();

function revealNow(el) {
  el.classList.add('in');
}

function init(root = document) {
  // scroll reveals
  const reveals = root.querySelectorAll('[data-recovr-reveal]');
  if (reduced) {
    reveals.forEach(revealNow);
  } else if (reveals.length) {
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            revealNow(entry.target);
            io.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12, rootMargin: '0px 0px -8% 0px' }
    );
    reveals.forEach((el, i) => {
      if (seen.has(el)) return;
      seen.add(el);
      el.style.transitionDelay = `${(i % 3) * 0.06}s`;
      io.observe(el);
    });
  }

  // survey / stat bars
  const bars = root.querySelectorAll('[data-recovr-bar]');
  const fill = (el) => {
    el.style.width = `${parseFloat(el.dataset.recovrBar) || 0}%`;
  };
  if (reduced) {
    bars.forEach(fill);
  } else if (bars.length) {
    const bo = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            fill(entry.target);
            bo.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.3 }
    );
    bars.forEach((el) => {
      if (seen.has(el)) return;
      seen.add(el);
      bo.observe(el);
    });
  }

  // progress rings — animate an SVG circle's stroke-dashoffset (pathLength=100)
  // via a CSS var, so the ring fills to N% on view. The .in class + the CSS
  // transition do the work; here we just flip the class (parity with reveals).
  const rings = root.querySelectorAll('[data-recovr-ring]');
  const ringIn = (el) => el.classList.add('in');
  if (reduced) {
    rings.forEach(ringIn);
  } else if (rings.length) {
    const ro = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            ringIn(entry.target);
            ro.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.35 }
    );
    rings.forEach((el) => {
      if (seen.has(el)) return;
      seen.add(el);
      ro.observe(el);
    });
  }

  // count-up numbers — [data-recovr-count="89"] eases 0 → target on view
  const counts = root.querySelectorAll('[data-recovr-count]');
  const setFinal = (el) => {
    el.textContent = `${parseFloat(el.dataset.recovrCount) || 0}`;
  };
  if (reduced) {
    counts.forEach(setFinal);
  } else if (counts.length) {
    const co = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          co.unobserve(entry.target);
          const el = entry.target;
          const to = parseFloat(el.dataset.recovrCount) || 0;
          const start = performance.now();
          const dur = 1400;
          const tick = (now) => {
            const p = Math.min((now - start) / dur, 1);
            const eased = 1 - Math.pow(1 - p, 3);
            el.textContent = `${Math.round(to * eased)}`;
            if (p < 1) requestAnimationFrame(tick);
          };
          requestAnimationFrame(tick);
        });
      },
      { threshold: 0.5 }
    );
    counts.forEach((el) => {
      if (seen.has(el)) return;
      seen.add(el);
      co.observe(el);
    });
  }

  // scroll-expand timeline steps
  const steps = root.querySelectorAll('[data-recovr-step]');
  if (reduced) {
    steps.forEach((el) => el.classList.add('on'));
  } else if (steps.length) {
    const so = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('on');
            so.unobserve(entry.target);
          }
        });
      },
      { rootMargin: '-45% 0px -45% 0px' }
    );
    steps.forEach((el) => {
      if (seen.has(el)) return;
      seen.add(el);
      so.observe(el);
    });
  }

  // sticky mobile bars (shown after their watch target scrolls away)
  root.querySelectorAll('[data-recovr-sticky]').forEach((bar) => {
    if (seen.has(bar)) return;
    seen.add(bar);
    const target = document.querySelector(bar.dataset.recovrSticky);
    if (!target) return;
    const po = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          bar.classList.toggle('show', !entry.isIntersecting);
        });
      },
      { threshold: 0 }
    );
    po.observe(target);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => init());
} else {
  init();
}

// keep working inside the theme editor
document.addEventListener('shopify:section:load', (event) => init(event.target));
